import { cn } from '@/lib/utils/cn'

export interface MdTab<V extends string = string> {
  id: V
  label: React.ReactNode
  /** Small count badge rendered right of the label. */
  count?: number
}

export interface MdTabsProps<V extends string = string> {
  tabs: MdTab<V>[]
  value: V
  onChange: (value: V) => void
  /** Keyboard shortcut hint displayed on focus (e.g. "⌘1"). The shell owns
   *  the actual shortcut binding via useKeyboardShortcut. */
  shortcutPrefix?: string
  className?: string
}

/**
 * Detail-pane tabs. Bottom-border accent on active. Used inside
 * MasterDetail headers and Drawer/Detail layouts. Not to be confused
 * with Radix/custom page-level tabs — those are banned per Principle 7.2
 * except in Admin module sub-sections.
 */
export function MdTabs<V extends string>({
  tabs,
  value,
  onChange,
  shortcutPrefix = '⌘',
  className,
}: MdTabsProps<V>) {
  return (
    <div
      role="tablist"
      className={cn('flex gap-1 border-b border-border-subtle bg-surface px-5', className)}
    >
      {tabs.map((tab, idx) => {
        const active = tab.id === value
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.id)}
            title={`${shortcutPrefix}${idx + 1}`}
            className={cn(
              '-mb-px cursor-pointer border-0 border-b-2 border-transparent bg-transparent px-3 py-2.5 text-sm font-medium text-secondary transition-colors duration-fast',
              'hover:text-primary',
              active && 'border-b-accent text-accent-text',
            )}
          >
            <span>{tab.label}</span>
            {typeof tab.count === 'number' ? (
              <span
                className={cn(
                  'ml-1.5 text-xs tabular-nums',
                  active ? 'text-accent-text opacity-70' : 'text-tertiary',
                )}
              >
                {tab.count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
